import { Link } from 'react-router-dom';
import type { BlogPost } from '../../../mocks/blog';
import { useTheme } from '../../../hooks/useTheme';

interface ArticleCardProps {
  post: BlogPost;
  featured?: boolean;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ArticleCard({ post, featured = false }: ArticleCardProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  if (featured) {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className={`group block rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer ${
          isLight ? 'bg-white border-slate-100 hover:border-electric/30' : 'bg-white/5 border-white/8 hover:border-electric/30'
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Image */}
          <div className="relative h-64 lg:h-auto overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
            {/* Featured badge */}
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase text-white"
                style={{ backgroundColor: '#00cfff' }}
              >
                <i className="ri-star-fill text-[9px]" />
                Destacado
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase"
                style={{ backgroundColor: post.categoryColor + '18', color: post.categoryColor }}
              >
                {post.category}
              </span>
              <span className={`text-xs font-body ${isLight ? 'text-slate-400' : 'text-white/35'}`}>
                {post.readTime} min lectura
              </span>
            </div>

            <h2 className={`font-heading text-xl sm:text-2xl font-bold mb-3 leading-snug group-hover:text-electric transition-colors duration-200 ${isLight ? 'text-navy-950' : 'text-white'}`}>
              {post.title}
            </h2>

            <p className={`font-body text-sm leading-relaxed mb-6 line-clamp-3 ${isLight ? 'text-navy-600' : 'text-white/55'}`}>
              {post.excerpt}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-navy-950 font-bold text-xs flex-shrink-0"
                  style={{ backgroundColor: '#00cfff' }}
                >
                  IF
                </div>
                <div>
                  <p className={`text-xs font-semibold font-body ${isLight ? 'text-navy-800' : 'text-white/80'}`}>{post.author}</p>
                  <p className={`text-[10px] font-body ${isLight ? 'text-slate-400' : 'text-white/35'}`}>{formatDate(post.date)}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 text-electric text-sm font-semibold font-body">
                Leer
                <div className="w-4 h-4 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-1">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer ${
        isLight ? 'bg-white border-slate-100 hover:border-electric/30' : 'bg-white/5 border-white/8 hover:border-electric/30'
      }`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase text-white"
            style={{ backgroundColor: post.categoryColor }}
          >
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-body ${isLight ? 'text-slate-400' : 'text-white/35'}`}>
            {formatDate(post.date)}
          </span>
          <span className={`text-white/20 ${isLight ? 'text-slate-300' : ''}`}>·</span>
          <span className={`text-xs font-body ${isLight ? 'text-slate-400' : 'text-white/35'}`}>
            {post.readTime} min
          </span>
        </div>

        <h3 className={`font-heading text-base font-bold mb-2 leading-snug group-hover:text-electric transition-colors duration-200 line-clamp-2 ${isLight ? 'text-navy-950' : 'text-white'}`}>
          {post.title}
        </h3>

        <p className={`font-body text-sm leading-relaxed mb-4 line-clamp-2 ${isLight ? 'text-navy-600' : 'text-white/50'}`}>
          {post.excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className={`px-2 py-0.5 rounded text-[10px] font-body ${
                isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/8 text-white/40'
              }`}
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3 border-t"
          style={{ borderColor: isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-1.5">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-navy-950 font-bold text-[10px] flex-shrink-0"
              style={{ backgroundColor: '#00cfff' }}
            >
              IF
            </div>
            <span className={`text-xs font-body ${isLight ? 'text-navy-600' : 'text-white/55'}`}>{post.author}</span>
          </div>
          <div className="flex items-center gap-1 text-electric text-xs font-semibold font-body">
            Leer artículo
            <div className="w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-1">
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
